import React from 'react';
import { APITypes } from '../../../../../../../primary-record-types';
import { useOrganizationId, useOrganizationRole } from '../../../../../../../hooks/api/useOrganization';
import { ActionButtonLayout } from '../../../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../../../utils/intl';
import Text from '../../../../../../v2BaseComponents/Text';
import Stack from '../../../../../../v2BaseComponents/Stack';
import ScaledImage from '../../../../../../v2BaseComponents/ScaledImage';
import { useAvatar } from '../../../../../../../hooks/useAvatar';
import urls from '../../../../../../../utils/urls';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import ContactDetailsDisplay from '../../../../../../shared/ContactDetailsDisplay';
import { Switch } from '../../../../../../v2BaseComponents/Inputs';
import { getSpacing } from '../../../../../../../utils/spacing';
import BrandingColorDisplay from './BrandingColorDisplay';

const intl = getIntl('OrganizationDetailsContent', {
    edit: 'Edit Organization',
    logoAlt: 'Organization logo',
    isPublic: 'Publicly Visible',
    publicExplainer:
        'Public visibility is required to show the organization name, description, and logo during the invitation process.',
    brandingPrimaryColorTitle: 'Brand Color',
    brandingSecondaryColorTitle: 'Accent Color',
});

export type OrganizationDetailsContentOwnProps = {
    organization: APITypes.organizationService.GetOrganizationMeResponse;
    onEdit: () => void;
};

export type OrganizationDetailsContentProps = OrganizationDetailsContentOwnProps;

const OrganizationDetailsContent: React.FC<OrganizationDetailsContentProps> = (props) => {
    const organizationId = useOrganizationId();
    const role = useOrganizationRole();
    const { avatar } = useAvatar({
        avatarAPIUrl: urls.api.organizations.logo(organizationId),
    });

    if (!props.organization) return null;

    return (
        <ActionButtonLayout
            scrollable
            actions={[
                {
                    title: intl('edit'),
                    onPress: props.onEdit,
                    show: role !== 'agent',
                    icon: 'Edit2',
                    testID: 'edit-organization-button',
                },
            ]}
            whiteBackground
        >
            <MainContentContainer>
                <Stack vertical fullWidth align="flex-start" space={16} testID="organization-details-content">
                    <Text variant="bold-h4">{props.organization.organization.metadata.name}</Text>
                    {avatar?.base64 ? (
                        <ScaledImage
                            uri={avatar?.base64 || null}
                            restrict="height"
                            alt={intl('logoAlt')}
                            containerStyle={{
                                height: 80,
                            }}
                        />
                    ) : null}
                    {props.organization.organization.metadata.description ? (
                        <Text variant="body-1">{props.organization.organization.metadata.description}</Text>
                    ) : null}
                    <Stack vertical space={4}>
                        <ContactDetailsDisplay contactDetails={props.organization.organization.contactDetails} />
                        {props.organization.organization.metadata.brandingPrimaryColor ? (
                            <BrandingColorDisplay
                                color={props.organization.organization.metadata.brandingPrimaryColor}
                                label={intl('brandingPrimaryColorTitle')}
                            />
                        ) : null}
                        {props.organization.organization.metadata.brandingSecondaryColor ? (
                            <BrandingColorDisplay
                                color={props.organization.organization.metadata.brandingSecondaryColor}
                                label={intl('brandingSecondaryColorTitle')}
                            />
                        ) : null}
                    </Stack>
                    <Stack vertical space={getSpacing(2)}>
                        <Switch
                            title={intl('isPublic')}
                            value={props.organization.organization.isPubliclyListed ?? false}
                            isDisabled
                            onChange={() => {}}
                        />
                        <Text variant="caption-2">{intl('publicExplainer')}</Text>
                    </Stack>
                </Stack>
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

export default React.memo(OrganizationDetailsContent);
